import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import z from "zod";
import { ChatInput } from "../../../../components/chatInput/ChatInput";
import { Button } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";

type CheckoutCustomCommentFormData = {
  deliveryInstructions: string;
  orderComment: string;
};

type CheckoutCustomCommentFormMode = "delivery-instructions" | "order-comments";

type CheckoutCustomCommentFormProps = {
  data: CheckoutCustomCommentFormData;
  initialMode: CheckoutCustomCommentFormMode;
  placeholder?: string;
  onSaveDeliveryInstructions: (deliveryInstructions: string) => void;
  onSaveOrderComment: (orderComment: string) => void;
};

const CheckoutCustomCommentFormSchemaZ = z.object({
  message: z.string(),
});

type CheckoutCustomCommentFormSchemaType = z.infer<
  typeof CheckoutCustomCommentFormSchemaZ
>;

export const CheckoutCustomCommentForm = ({
  data,
  initialMode,
  placeholder = "Add a message",
  onSaveDeliveryInstructions,
  onSaveOrderComment,
}: CheckoutCustomCommentFormProps) => {
  const [mode, setMode] = useState<CheckoutCustomCommentFormMode>(initialMode);

  const formDefaultValues = computeFormDefaultValues({
    data,
    mode,
  });

  const form = useForm({
    defaultValues: formDefaultValues,
    validators: {
      onChange: CheckoutCustomCommentFormSchemaZ,
    },
    onSubmit: ({ value }) => {
      handleSaveForm(CheckoutCustomCommentFormSchemaZ.parse(value));
    },
  });

  const handleSaveForm = (data: CheckoutCustomCommentFormSchemaType) => {
    if (mode === "delivery-instructions") {
      onSaveDeliveryInstructions(data.message);
      return;
    }
    if (mode === "order-comments") {
      onSaveOrderComment(data.message);
      return;
    }

    console.error("Something went wrong");
  };

  const canShowDeliveryInstructionsInformation =
    mode === "delivery-instructions";

  return (
    <div className="flex flex-col gap-4">
      <TabSelector tab={mode} onChangeTab={setMode} />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <form.Field name="message">
          {({ state, handleChange }) => {
            return (
              <div>
                <ChatInput
                  value={state.value}
                  setValue={handleChange}
                  placeholder={placeholder}
                  rows={4}
                  maxRows={8}
                />
                <FieldError errorMessage={state.meta.errors[0]?.message} />
              </div>
            );
          }}
        </form.Field>

        {canShowDeliveryInstructionsInformation && (
          <div className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-4">
            <p>This message will be</p>
            <ul className="flex list-inside list-disc flex-col">
              <li>Saved to your business</li>
              <li>Shared with all suppliers</li>
              <li>Applied to every future order</li>
            </ul>
          </div>
        )}

        <Button type="submit" className="w-full">
          Save
        </Button>
      </form>
    </div>
  );
};

const FieldError = ({ errorMessage }: { errorMessage: string | undefined }) => {
  if (!errorMessage) {
    return null;
  }
  return (
    <span role="alert" className="text-sm text-red-700">
      {errorMessage}
    </span>
  );
};

const computeFormDefaultValues = ({
  data,
  mode,
}: Pick<CheckoutCustomCommentFormProps, "data"> & {
  mode: CheckoutCustomCommentFormMode;
}): CheckoutCustomCommentFormSchemaType => {
  const { deliveryInstructions, orderComment } = data;
  if (mode === "order-comments") {
    return {
      message: orderComment,
    };
  }

  if (mode === "delivery-instructions") {
    return {
      message: deliveryInstructions,
    };
  }

  return {
    message: "",
  };
};

const tabOptions: Array<{ tab: CheckoutCustomCommentFormMode; label: string }> =
  [
    { tab: "order-comments", label: "Add an order comment" },
    {
      tab: "delivery-instructions",
      label: "Add delivery instructions",
    },
  ];

type TabSelectorProps = {
  tab: CheckoutCustomCommentFormMode;
  onChangeTab: (tab: CheckoutCustomCommentFormMode) => void;
};

const TabSelector = ({ tab, onChangeTab }: TabSelectorProps) => {
  return (
    <div className="flex transition-all">
      {tabOptions.map((tabOption) => {
        const isActive = tab === tabOption.tab;
        return (
          <button
            type="button"
            key={tabOption.tab}
            className={twMerge(
              "flex-1 p-4 transition-colors",
              isActive
                ? "border-b-3 border-b-zinc-800 font-semibold text-black"
                : "cursor-pointer border-b-2 border-b-zinc-300 text-zinc-500",
            )}
            onClick={() => {
              onChangeTab(tabOption.tab);
            }}
          >
            {tabOption.label}
          </button>
        );
      })}
    </div>
  );
};
